import { Theme, SxProps } from '@mui/material/styles';

/** Background used by the Reports page toolbar (server / suite / time pickers). */
export const reportsToolbarSurfaceBg = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)';

export const reportsToolbarSurfaceSx = {
  borderRadius: 2,
  backgroundColor: reportsToolbarSurfaceBg,
} as const;

/** DEFAULT + PROFESSIONAL light: secondary gold remaps to low-contrast greys on toolbar paper. */
export const isReportsToolbarLightAccent = (theme: Theme): boolean =>
  theme.palette.mode === 'light';

export const reportsToolbarAccentColor = (theme: Theme): string =>
  isReportsToolbarLightAccent(theme) ? 'rgb(25, 25, 25)' : theme.palette.secondary.main;

export const reportsToolbarAccentBorder = (theme: Theme): string =>
  isReportsToolbarLightAccent(theme) ? 'rgba(25, 25, 25, 0.38)' : theme.palette.secondary.main;

/** Zoom / shift chevrons on the suite time-range control strip. */
export const reportsTimeRangeIconButtonSx = (theme: Theme): SxProps<Theme> => ({
  border: '1px solid',
  borderColor: reportsToolbarAccentBorder(theme),
  color: reportsToolbarAccentColor(theme),
  borderRadius: 1,
  '& .MuiSvgIcon-root': { color: reportsToolbarAccentColor(theme) },
  ...(isReportsToolbarLightAccent(theme) && {
    '&.Mui-disabled': {
      borderColor: 'rgba(25, 25, 25, 0.26)',
      color: 'rgba(25, 25, 25, 0.38)',
      '& .MuiSvgIcon-root': { color: 'rgba(25, 25, 25, 0.38)' },
    },
  }),
});

/** Outlined pill showing the active preset / custom range label. */
export const reportsTimeRangePickerButtonSx = (theme: Theme): SxProps<Theme> => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.82rem',
  borderColor: reportsToolbarAccentBorder(theme),
  color: reportsToolbarAccentColor(theme),
  px: 1.5,
  whiteSpace: 'nowrap',
  '& .MuiSvgIcon-root': { color: reportsToolbarAccentColor(theme) },
});

/**
 * Indeterminate toolbar strip shown while server / suite dropdown data loads.
 * PROFESSIONAL dark: gold (secondary.main); PROFESSIONAL light: black.
 */
export const reportsToolbarLoadingLinearProgressSx = (theme: Theme): SxProps<Theme> => {
  const accent = reportsToolbarAccentColor(theme);
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderRadius: '4px 4px 0 0',
    height: 2,
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(25, 25, 25, 0.12)'
      : 'rgba(255, 255, 255, 0.12)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: accent,
    },
  };
};

/** Refresh / menu IconButtons on the main Reports toolbar. */
export const reportsToolbarIconButtonSx = (theme: Theme): SxProps<Theme> => {
  if (!isReportsToolbarLightAccent(theme)) {
    return {};
  }
  return {
    color: 'rgb(25, 25, 25)',
    border: '1px solid',
    borderColor: 'rgba(25, 25, 25, 0.38)',
    borderRadius: 1,
    '& .MuiSvgIcon-root': { color: 'rgb(25, 25, 25)' },
    '&:hover': {
      borderColor: 'rgb(25, 25, 25)',
      backgroundColor: 'rgba(25, 25, 25, 0.06)',
    },
  };
};

/**
 * Back navigation on Direct Reports drill-down views (level-1 / level-2).
 * Default `textPrimary` is unreadable under PROFESSIONAL light/dark colour remaps;
 * mirror breadcrumb-link (light) and gold-accent (dark) styling instead.
 */
export const reportsDrilldownBackButtonSx = (theme: Theme): SxProps<Theme> => {
  const isProfessional = Boolean(theme.palette.dashboard);
  const bc = theme.palette.breadcrumb;

  if (isProfessional && theme.palette.mode === 'light' && bc) {
    return {
      textTransform: 'none',
      fontWeight: 600,
      color: bc.linkColor,
      backgroundColor: bc.linkBg,
      border: '1px solid',
      borderColor: bc.linkBorderColor,
      borderRadius: 1,
      px: 1.5,
      boxShadow: 'none',
      '& .MuiSvgIcon-root': { color: bc.linkColor },
      '&:hover': {
        backgroundColor: bc.linkHoverBg,
        borderColor: bc.linkBorderColor,
        boxShadow: 'none',
      },
    };
  }

  if (isProfessional && theme.palette.mode === 'dark') {
    const accent = theme.palette.secondary?.main ?? 'rgb(255, 209, 102)';
    return {
      textTransform: 'none',
      fontWeight: 600,
      color: accent,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
      border: '1px solid',
      borderColor: accent,
      borderRadius: 1,
      px: 1.5,
      boxShadow: 'none',
      '& .MuiSvgIcon-root': { color: accent },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        boxShadow: 'none',
      },
    };
  }

  if (theme.palette.mode === 'light') {
    return {
      textTransform: 'none',
      fontWeight: 600,
      color: 'rgb(25, 25, 25)',
      border: '1px solid',
      borderColor: 'rgba(25, 25, 25, 0.38)',
      borderRadius: 1,
      px: 1.5,
      boxShadow: 'none',
      '& .MuiSvgIcon-root': { color: 'rgb(25, 25, 25)' },
      '&:hover': {
        backgroundColor: 'rgba(25, 25, 25, 0.06)',
        boxShadow: 'none',
      },
    };
  }

  const accent = theme.palette.secondary.main;
  return {
    textTransform: 'none',
    fontWeight: 600,
    color: accent,
    border: '1px solid',
    borderColor: accent,
    borderRadius: 1,
    px: 1.5,
    boxShadow: 'none',
    '& .MuiSvgIcon-root': { color: accent },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      boxShadow: 'none',
    },
  };
};

/** Small outlined square buttons on per-report panel toolbars (Refresh, Edit, …). */
export const reportsPanelToolbarButtonSx = (
  theme: Theme,
  base: SxProps<Theme> = {},
): SxProps<Theme> => {
  if (!isReportsToolbarLightAccent(theme)) {
    return base;
  }
  return {
    ...base,
    color: 'rgb(25, 25, 25)',
    borderColor: 'rgba(25, 25, 25, 0.38)',
    '& .MuiSvgIcon-root': { color: 'rgb(25, 25, 25)' },
    '&:hover': {
      borderColor: 'rgb(25, 25, 25)',
      backgroundColor: 'rgba(25, 25, 25, 0.06)',
    },
  };
};
